import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type KeyboardEvent as ReactKeyboardEvent,
    type RefObject,
} from 'react';
import {
    DEFAULT_DOCK_ANCHOR,
    DOCK_NUDGE_FAST_PX,
    DOCK_NUDGE_PX,
    clampDockOffset,
    describeDockPosition,
    fractionToOffset,
    measureDockAnchor,
    readStoredDockPosition,
    writeStoredDockPosition,
    type DockAnchor,
    type DockBox,
    type DockOffset,
    type StoredDockPosition,
} from '@/components/FloatingActionBar/floatingActionBarPosition';

const DEFAULT_OFFSET: DockOffset = { x: 0, y: 0 };
/**
 * Seed until the ResizeObserver reports a real size, deliberately the smaller mobile pill.
 * Under-estimating only tightens the bounds for a frame, where over-estimating would let the bar
 * sit outside them.
 */
const FALLBACK_DOCK_SIZE: DockBox = { width: 54, height: 120 };
const ANNOUNCE_DEBOUNCE_MS = 500;

/**
 * There is no viewport during a server render, so fall back to the seed size until the client
 * measures. The bar is repositioned on mount either way.
 */
const viewportNow = (): DockBox => (typeof window === 'undefined'
    ? FALLBACK_DOCK_SIZE
    : { width: window.innerWidth, height: window.innerHeight });

const NUDGE_DELTAS: Record<string, { x: number; y: number }> = {
    ArrowLeft: { x: -1, y: 0 },
    ArrowRight: { x: 1, y: 0 },
    ArrowUp: { x: 0, y: -1 },
    ArrowDown: { x: 0, y: 1 },
};

export interface UseFloatingActionBarPositionResult {
    /** The committed offset. Mid-gesture the live delta is added on top by the caller. */
    position: DockOffset;
    /** Measured border-box size of the bar so the home-position ghost can match it. */
    size: DockBox;
    announcement: string;
    /** Holds an offset inside the viewport. */
    clampOffset: (offset: DockOffset) => DockOffset;
    onDragStart: () => void;
    onDragEnd: (delta: DockOffset) => void;
    onKeyDown: (event: ReactKeyboardEvent) => void;
}

export const useFloatingActionBarPosition = (
    userId: string,
    nodeRef: RefObject<HTMLElement | null>,
): UseFloatingActionBarPositionResult => {
    // Measured lazily. A zero-size reading means hidden (or jsdom) and is ignored below.
    const sizeRef = useRef<DockBox>(FALLBACK_DOCK_SIZE);
    const anchorRef = useRef<DockAnchor>(DEFAULT_DOCK_ANCHOR);

    // Starts home and is restored in a layout effect below rather than in this initializer.
    const [position, setPosition] = useState<DockOffset>(DEFAULT_OFFSET);
    const [size, setSize] = useState<DockBox>(FALLBACK_DOCK_SIZE);
    const [announcement, setAnnouncement] = useState('');

    const announceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Mirrors the committed position so measure() can subtract the drag transform back out without
    // re-running on every move.
    const positionRef = useRef<DockOffset>(position);
    /** The saved position, held until a measurement it can honestly be resolved against arrives. */
    const restoreFractionRef = useRef<StoredDockPosition | null>(null);
    const restoredForRef = useRef<string | null>(null);

    useEffect(() => {
        positionRef.current = position;
    }, [position]);

    const commit = useCallback((next: DockOffset) => {
        // The user has taken over so the saved fraction stops being authoritative.
        restoreFractionRef.current = null;
        setPosition(next);
        writeStoredDockPosition(userId, next, viewportNow(), sizeRef.current, anchorRef.current);
    }, [userId]);

    /**
     * Re-reads the bar's own geometry: the border-box size the user sees, and the resting inset,
     * which the safe-area inset makes impossible to hardcode. A zero rect means the bar is hidden
     * (a sheet is open) or jsdom, and caching that would inflate the travel and let a later clamp
     * walk the bar off-screen, so both readings keep their previous value.
     */
    const measure = useCallback(() => {
        const node = nodeRef.current;
        if (!node) {
            return;
        }
        const rect = node.getBoundingClientRect();
        const viewport = viewportNow();
        const anchor = measureDockAnchor(rect, viewport, positionRef.current);
        if (anchor) {
            anchorRef.current = anchor;
        }
        const hasChangedSize = rect.width !== sizeRef.current.width
            || rect.height !== sizeRef.current.height;
        if (rect.width > 0 && rect.height > 0 && hasChangedSize) {
            sizeRef.current = { width: rect.width, height: rect.height };
            setSize(sizeRef.current);
        }

        // A saved position is a share of the available travel, so it is only as good as the size
        // it resolves against, and the pill has no settled size until ZigBot's access check
        // returns. Re-resolving on every measurement lets it land right whenever that happens.
        const stored = restoreFractionRef.current;
        if (stored) {
            const restored = fractionToOffset(stored, viewport, sizeRef.current, anchorRef.current);
            positionRef.current = restored;
            setPosition(current =>
                (current.x === restored.x && current.y === restored.y ? current : restored));
        }
    }, [nodeRef]);

    const clampOffset = useCallback((offset: DockOffset): DockOffset =>
        clampDockOffset(offset, viewportNow(), sizeRef.current, anchorRef.current), []);

    /**
     * Picks up the saved position once the id it was saved under is known. The id comes from a
     * query, so a useState initializer would look under the 'anonymous' key and never find it.
     * Applying it is measure()'s job, which keeps doing so until the user moves the bar themselves.
     * A layout effect so the first attempt lands before paint rather than jumping a frame later.
     */
    useLayoutEffect(() => {
        if (restoredForRef.current === userId) {
            return;
        }
        restoredForRef.current = userId;
        // With nothing saved the bar stays at its home anchor.
        restoreFractionRef.current = readStoredDockPosition(userId);
        measure();
    }, [userId, measure]);

    useLayoutEffect(() => {
        const node = nodeRef.current;
        if (!node || typeof ResizeObserver !== 'function') {
            return;
        }
        const observer = new ResizeObserver(() => measure());
        observer.observe(node);
        return () => observer.disconnect();
    }, [nodeRef, measure]);

    useEffect(() => {
        const onViewportChange = () => {
            // Re-measures the anchor too. Rotating a phone changes which edge carries the
            // safe-area inset, so the resting inset is not constant across an orientation change.
            measure();
            // clampDockOffset returns the same object when nothing moved so an ordinary window
            // resize doesn't re-render on every frame and needs no debounce.
            setPosition(current =>
                clampDockOffset(current, viewportNow(), sizeRef.current, anchorRef.current));
        };
        window.addEventListener('resize', onViewportChange);
        // iOS has fired orientationchange with stale dimensions. The resize that follows corrects
        // it, and the update is idempotent so running twice is harmless.
        window.addEventListener('orientationchange', onViewportChange);
        return () => {
            window.removeEventListener('resize', onViewportChange);
            window.removeEventListener('orientationchange', onViewportChange);
        };
    }, [measure]);

    useEffect(() => () => {
        if (announceTimerRef.current !== null) {
            clearTimeout(announceTimerRef.current);
        }
    }, []);

    const onDragStart = useCallback(() => {
        measure();
    }, [measure]);

    const onDragEnd = useCallback((delta: DockOffset) => {
        commit(clampOffset({
            x: positionRef.current.x + delta.x,
            y: positionRef.current.y + delta.y,
        }));
    }, [commit, clampOffset]);

    const announceLater = useCallback((next: DockOffset) => {
        if (announceTimerRef.current !== null) {
            clearTimeout(announceTimerRef.current);
        }
        announceTimerRef.current = setTimeout(() => {
            const where = describeDockPosition(
                next,
                viewportNow(),
                sizeRef.current,
                anchorRef.current,
            );
            setAnnouncement(`Quick actions moved to the ${where}`);
        }, ANNOUNCE_DEBOUNCE_MS);
    }, []);

    const onKeyDown = useCallback((event: ReactKeyboardEvent) => {
        if (event.key === 'Home') {
            event.preventDefault();
            commit(DEFAULT_OFFSET);
            setAnnouncement('Quick actions returned to the bottom right');
            return;
        }
        const direction = NUDGE_DELTAS[event.key];
        if (!direction) {
            return;
        }
        event.preventDefault();
        const step = event.shiftKey ? DOCK_NUDGE_FAST_PX : DOCK_NUDGE_PX;
        const next = clampOffset({
            x: position.x + direction.x * step,
            y: position.y + direction.y * step,
        });
        commit(next);
        announceLater(next);
    }, [commit, clampOffset, position, announceLater]);

    return {
        position,
        size,
        announcement,
        clampOffset,
        onDragStart,
        onDragEnd,
        onKeyDown,
    };
};
